import { Field, makeStyles, Textarea } from '@fluentui/react-components';
import React, { useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { DetailedWorkspaceDto } from '../../../model';
import { IFormViewerMethods, ImperativeComponent } from '../../../utils/DetailedWorkspaceUtils';

export interface ITextboxFormViewerProps {
    detailedWorkspace: DetailedWorkspaceDto | null | undefined;
    onDataChange?: () => void;
}

const useStyles = makeStyles({
    textAreaContainer: {
        height: '100%',
        width: '100%',
    },
    subTitle: { paddingBottom: '10px' },
    tableContentClass: {
        display: 'flex',
        flex: '1 1 auto',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
    },
});

const TextboxFormViewer: ImperativeComponent<IFormViewerMethods, ITextboxFormViewerProps> = React.forwardRef<
    IFormViewerMethods,
    ITextboxFormViewerProps
>(({ detailedWorkspace, onDataChange }, textBoxRef) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { functionalAccess } = useFunctionalAccess();
    useImperativeHandle(textBoxRef, () => ({
        getUpdatedDetailedWorkspace,
        cleanUpState: cleanUpState,
        focus: () => textareaRef.current?.focus(),
    }));

    const styles = useStyles();
    const { t } = useTranslation();
    const [textboxValue, setTextboxValue] = React.useState<string | undefined>(undefined);

    const getUpdatedDetailedWorkspace = () => {
        const updatedDetailedWorkspaceDto: DetailedWorkspaceDto = {
            ...detailedWorkspace,
            text: textboxValue ?? detailedWorkspace?.text ?? '',
        };

        return updatedDetailedWorkspaceDto;
    };

    const cleanUpState = () => {
        setTextboxValue(undefined);
    };

    if (!detailedWorkspace) {
        return <div className={styles.tableContentClass}>{t('noDataAvailable')}</div>;
    }

    const initialTextAreaValue = textboxValue ?? detailedWorkspace?.text ?? '';

    const updateTextboxValue = (value: string | undefined) => {
        setTextboxValue(value);
        onDataChange?.();
    };

    return (
        <div className={styles.tableContentClass}>
            <Field className={styles.textAreaContainer}>
                <Textarea
                    disabled={functionalAccess?.amountDataEntryModifyEnabled === false}
                    ref={textareaRef}
                    value={textboxValue ?? initialTextAreaValue}
                    onChange={(e) => updateTextboxValue(e.target.value)}
                    placeholder={t('addNotesHere').toString()}
                />
            </Field>
        </div>
    );
});

export default TextboxFormViewer;
